import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    mensaje: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Solicitud enviada",
      description: "Nos pondremos en contacto con usted a la mayor brevedad.",
    });
    setFormData({ nombre: '', email: '', telefono: '', mensaje: '' });
  };

  return (
    <section ref={sectionRef} id="cita" className="py-24 bg-charcoal">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left column - Info */}
          <div className="animate-on-scroll">
            <p className="eyebrow text-gold mb-6">Contacto</p>
            
            <h2 className="font-serif text-4xl md:text-5xl text-ivory mb-8">
              Solicite su<br />
              <span className="text-gold">consulta</span>
            </h2>
            
            <div className="w-16 h-px bg-gold mb-10"></div>
            
            <p className="text-gold-light/70 leading-relaxed mb-12">
              El equipo del Dr. Norro le atenderá para estudiar su caso, 
              resolver sus dudas y planificar la consulta de forma totalmente confidencial.
              También disponemos de videoconsulta.
            </p>
            
            <div className="space-y-6">
              <a 
                href="tel:+34952764547" 
                className="flex items-center gap-4 text-gold-light hover:text-gold transition-colors group"
              >
                <span className="w-12 h-12 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-all">
                  <Phone size={18} />
                </span>
                <div>
                  <p className="text-xs text-text-muted uppercase tracking-wider">Teléfono</p>
                  <p className="text-lg">+34 952 76 45 47</p>
                </div>
              </a>
              
              <a 
                href="mailto:info@drangelnorro.com" 
                className="flex items-center gap-4 text-gold-light hover:text-gold transition-colors group"
              >
                <span className="w-12 h-12 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-all">
                  <Mail size={18} />
                </span>
                <div>
                  <p className="text-xs text-text-muted uppercase tracking-wider">Email</p>
                  <p className="text-lg">info@drangelnorro.com</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 text-gold-light">
                <span className="w-12 h-12 border border-gold/30 flex items-center justify-center">
                  <MapPin size={18} />
                </span>
                <div>
                  <p className="text-xs text-text-muted uppercase tracking-wider">Dirección</p>
                  <p className="text-lg">C/ Carlos Mackintosh, 3<br/>29602 Marbella, Málaga</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Form */}
          <div className="animate-on-scroll">
            <div className="bg-ivory p-8 md:p-10">
              <h3 className="font-serif text-2xl text-charcoal mb-6">
                Formulario de contacto
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="input-luxury w-full"
                    placeholder="Su nombre"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                      className="input-luxury w-full"
                      placeholder="Su teléfono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-luxury w-full"
                      placeholder="su@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                    Mensaje
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows={4}
                    className="input-luxury w-full resize-none"
                    placeholder="Cuéntenos brevemente su consulta..."
                  />
                </div>
                
                <button 
                  type="submit"
                  className="btn-gold w-full flex items-center justify-center gap-2 group"
                >
                  Enviar solicitud
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
              
              <p className="text-xs text-text-muted text-center mt-6">
                La información ofrecida en esta página no sustituye a una consulta médica particularizada.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
